import { ELK, ElkNode } from "../lib/elk.bundled";
import { CanvasLayout, Pane, PaneType, Position, Rect } from "../Types";
import { MAGIC_LAYOUT_ID } from "./SandboxConstants";
import {
  addPositions,
  getPaneIdFromPaneInput,
  roundPositionToGrid,
} from "./SandboxUtils";

export function isCustomLayoutId(layoutId: string | undefined): boolean {
  return layoutId !== undefined && layoutId !== MAGIC_LAYOUT_ID;
}

let elk: ELK | undefined;
async function getElk(): Promise<ELK> {
  if (elk !== undefined) {
    return elk;
  }
  const elkjs = await import("../lib/elk.bundled");
  return new elkjs.default();
}

async function elkLayout(g: ElkNode) {
  const elk = await getElk();
  await elk.layout(g);
}

export async function computeElkLayout(
  panes: Pane[],
  focusInfo: [string, CanvasLayout | undefined] | undefined
): Promise<Record<string, Rect>> {
  const children: any = [];
  const focusGroupInputPaneIds = new Set<string>();
  const focusGroupOutputPaneIds = new Set<string>();
  let focusNode;
  let focusChildren: any[] | undefined;
  let focusEdges: any[] | undefined;
  const focusPaneId = focusInfo !== undefined ? focusInfo[0] : undefined;
  const focusPane =
    focusPaneId !== undefined
      ? panes.find((p) => p.id === focusPaneId)!
      : undefined;
  if (focusPaneId !== undefined && focusPane !== undefined) {
    if (focusPane.type === PaneType.Evaluate) {
      focusPane.inputs.forEach((paneInput) => {
        const inputPaneId = getPaneIdFromPaneInput(paneInput);
        if (inputPaneId !== undefined) {
          focusGroupInputPaneIds.add(inputPaneId);
        }
      });
    }
    panes.forEach((p) => {
      if (p.type === PaneType.Evaluate) {
        if (
          p.inputs !== undefined &&
          p.inputs.some(
            (paneInput) => getPaneIdFromPaneInput(paneInput) === focusPaneId
          )
        ) {
          focusGroupOutputPaneIds.add(p.id);
        }
      }
    });
    focusChildren = [];
    focusEdges = [];
    focusNode = {
      id: "focus",
      children: focusChildren,

      layoutOptions: {
        "elk.padding": "[top=32,left=64,bottom=32,right=64]",
        "elk.spacing.nodeNode": "64",
        "elk.layered.spacing.nodeNodeBetweenLayers": "48",
        "partitioning.activate": "true",
      },
      edges: focusEdges,
    };
    children.push(focusNode);
  }
  panes.forEach((p) => {
    const node = {
      id: `p${p.id}`,
      width: p.rect[2],
      height: p.rect[3],

      ports: ("inputs" in p && p.inputs.length > 0
        ? p.inputs.map((paneInput, i) => {
            return {
              id: `p${p.id}_${
                paneInput.source === undefined
                  ? `empty${i}`
                  : `${paneInput.source[0]}_${paneInput.source[1]}`
              }`,
              x: -1,
              y: i * 8,
            };
          })
        : []
      ).concat([{ id: `p${p.id}_output`, x: p.rect[2] + 1, y: 0 }]),
    };
    if (focusGroupInputPaneIds.has(p.id)) {
      focusChildren!.push({
        ...node,
        layoutOptions: {
          "partitioning.partition": "1",
        },
      });
    } else if (p.id === focusPaneId) {
      focusChildren!.push({
        ...node,
        layoutOptions: {
          "partitioning.partition": "2",
        },
      });
    } else if (focusGroupOutputPaneIds.has(p.id)) {
      focusChildren!.push({
        ...node,
        layoutOptions: {
          "partitioning.partition": "3",
        },
      });
    } else {
      children.push(node);
    }
  });

  const edges: any = [];
  panes.forEach((p) => {
    if (p.type === PaneType.Evaluate) {
      p.inputs.forEach((paneInput, i) => {
        const inputPaneId = getPaneIdFromPaneInput(paneInput);
        if (inputPaneId === undefined) {
          return;
        }
        const isInputPaneInFocus =
          inputPaneId === focusPaneId ||
          focusGroupInputPaneIds.has(inputPaneId) ||
          focusGroupOutputPaneIds.has(inputPaneId);
        const isOutputPaneInFocus =
          p.id === focusPaneId ||
          focusGroupInputPaneIds.has(p.id) ||
          focusGroupOutputPaneIds.has(p.id);
        if (isInputPaneInFocus && isOutputPaneInFocus) {
          focusEdges!.push({
            id: `e${focusEdges!.length}`,
            sources: [`p${inputPaneId}_output`],
            targets: [
              `p${p.id}_${paneInput.source![0]}_${paneInput.source![1]}`,
            ],
          });
        } else if (isInputPaneInFocus) {
          edges.push({
            id: `e${edges.length}`,
            sources: [`focus`],
            targets: [
              `p${p.id}_${paneInput.source![0]}_${paneInput.source![1]}`,
            ],
          });
        } else if (isOutputPaneInFocus) {
          edges.push({
            id: `e${edges.length}`,
            sources: [`p${inputPaneId}_output`],
            targets: [`focus`],
          });
        } else {
          edges.push({
            id: `e${edges.length}`,
            sources: [`p${inputPaneId}_output`],
            targets: [
              `p${p.id}_${paneInput.source![0]}_${paneInput.source![1]}`,
            ],
          });
        }
      });
    }
  });

  const g = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.padding": "[left=32, top=32, right=0, bottom=0]",
      "elk.spacing.nodeNode": "64",
      "elk.layered.spacing.nodeNodeBetweenLayers": "48",
      "elk.layered.compaction.connectedComponents": "true",
    },
    children,
    edges,
  };

  await elkLayout(g);
  const paneRects: Record<string, Rect> = {};
  const focusNodeOutput = g.children.find((child: any) => child.id === "focus");

  let focusPaneOffset: Position = [0, 0];
  if (focusPane !== undefined) {
    const focusPanePosition = focusNodeOutput.children.find(
      (child: any) => child.id === `p${focusPane.id}`
    );
    const focusPaneLayoutPosition = roundPositionToGrid(
      [
        focusNodeOutput.x + focusPanePosition.x,
        focusNodeOutput.y + focusPanePosition.y,
      ],
      1
    );
    let focusPanePrevRect = focusPane.rect;
    const focusPaneLayoutMetadata = focusInfo![1]?.panes.find(
      (p) => p.id === focusPane.id
    );
    if (focusPaneLayoutMetadata !== undefined) {
      focusPanePrevRect = focusPaneLayoutMetadata.rect;
    }
    focusPaneOffset = roundPositionToGrid(
      [
        window.innerWidth / 2 - focusPaneLayoutPosition[0],
        window.innerHeight / 2 - focusPaneLayoutPosition[1],
      ],
      1
    );
  }

  panes.forEach((p) => {
    let x;
    let y;
    const node = g.children.find((child: any) => child.id === `p${p.id}`);
    if (node !== undefined) {
      x = node.x;
      y = node.y;
    } else {
      const focusChild = focusNodeOutput.children.find(
        (child: any) => child.id === `p${p.id}`
      )!;
      x = focusNodeOutput.x + focusChild.x;
      y = focusNodeOutput.y + focusChild.y;
    }
    paneRects[p.id] = [
      ...addPositions(roundPositionToGrid([x, y], 1), focusPaneOffset),
      p.rect[2],
      p.rect[3],
    ];
  });
  return paneRects;
}
